import React from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import {
  TrendingUp,
  Shield,
  Heart,
  FileText,
  Home as HomeIcon,
  PieChart,
  Layers,
  Target,
  BarChart3,
} from 'lucide-react';
import { MF_DISCLAIMER, COMMISSION_DISCLOSURE } from '@/components/ComplianceNotice';

const SERVICES = {
  'mutual-funds': {
    icon: TrendingUp,
    title: 'Mutual Fund Distribution Services',
    hero: 'Mutual fund distribution and investor assistance for goal-based, disciplined investing and long-term wealth creation opportunities.',
    note: `${COMMISSION_DISCLOSURE} ${MF_DISCLAIMER}`,
    image:
      'https://images.unsplash.com/photo-1541140911322-98afe66ea6da?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHx3ZWFsdGglMjBtYW5hZ2VtZW50JTIwZ29hbCUyMHBsYW5uaW5nfGVufDB8fHx8MTc4NDg5MjI4OHww&ixlib=rb-4.1.0&q=85',
    intro: [
      'Our mutual fund services focus on helping investors build and maintain portfolios aligned with their financial goals, risk profile and investment horizon.',
      'We share mutual fund scheme information and comparisons, facilitate SIP and lump-sum investments, and assist with periodic portfolio reviews so that your investments can remain aligned with your evolving needs.',
    ],
    cards: [
      {
        icon: Target,
        title: 'Goal-Based Planning',
        items: ['Retirement planning', "Children's education", 'Wealth creation', 'Home purchase'],
      },
      {
        icon: TrendingUp,
        title: 'Investment Methods',
        items: ['SIP (Systematic Investment Plan)', 'Lump-sum investments', 'STP (Systematic Transfer Plan)', 'SWP (Systematic Withdrawal Plan)'],
      },
      {
        icon: BarChart3,
        title: 'Portfolio Services',
        items: ['Scheme information and comparison', 'Periodic portfolio review', 'Asset allocation information', 'Rebalancing assistance'],
      },
    ],
    ctaTitle: 'Ready to Start Your Mutual Fund Journey?',
  },
  'fixed-deposits': {
    icon: Shield,
    title: 'Fixed Deposit Services',
    hero: 'Reliable fixed-income options selected on safety, tenure and liquidity needs.',
    note: 'Fixed deposits are not mutual fund products and are not covered by our AMFI registration (ARN: 116347). Deposits are accepted by the respective bank or company and are subject to its terms, credit risk and applicable regulations.',
    image:
      'https://images.pexels.com/photos/8353820/pexels-photo-8353820.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    intro: [
      'Fixed deposits remain a preferred choice for investors seeking capital protection and predictable income. We help you evaluate bank and corporate FD options based on safety, credit quality and payout preference.',
      'Our guidance covers tenure selection, interest payout options, tax implications and how FDs may fit alongside other fixed-income instruments in your portfolio.',
    ],
    cards: [
      {
        icon: Shield,
        title: 'FD Categories',
        items: ['Bank fixed deposits', 'Corporate fixed deposits', 'Cumulative deposits', 'Non-cumulative deposits'],
      },
      {
        icon: Target,
        title: 'Selection Criteria',
        items: ['Credit rating & safety', 'Tenure alignment with goals', 'Interest payout frequency', 'Liquidity & premature exit'],
      },
      {
        icon: BarChart3,
        title: 'Considerations',
        items: ['Tax on interest income', 'Reinvestment planning', 'Comparison with debt funds', 'Portfolio-level allocation'],
      },
    ],
    ctaTitle: 'Discuss Fixed-Income Options',
  },
  insurance: {
    icon: Heart,
    title: 'Insurance Guidance',
    hero: 'Protection-first planning for life, health and family financial security.',
    note: 'Insurance is not a mutual fund product and is not covered by our AMFI registration (ARN: 116347). Insurance products are offered by the respective insurer and arranged through appropriately IRDAI-registered channels, subject to policy terms and conditions.',
    image:
      'https://images.pexels.com/photos/8441812/pexels-photo-8441812.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    intro: [
      'Insurance is the foundation of every financial plan. We help you understand life and health protection needs and identify solutions that address family security without mixing investment and insurance objectives.',
      'Our approach begins with a need-based cover assessment, so you buy the right amount of protection for the right term at the right cost.',
    ],
    cards: [
      {
        icon: Heart,
        title: 'Life Protection',
        items: ['Pure-term life cover', 'Family income replacement', 'Loan-linked cover', 'Rider evaluation'],
      },
      {
        icon: Shield,
        title: 'Health Cover',
        items: ['Individual health plans', 'Family floater plans', 'Critical illness cover', 'Top-up & super top-up plans'],
      },
      {
        icon: Target,
        title: 'Our Approach',
        items: ['Need-based cover analysis', 'Separate insurance from investment', 'Existing policy review', 'Claims process guidance'],
      },
    ],
    ctaTitle: 'Review Your Protection Cover',
  },
  bonds: {
    icon: FileText,
    title: 'Bond Investments',
    hero: 'Fixed-income opportunities for income, diversification and capital stability.',
    note: 'Bonds are not mutual fund products and are not covered by our AMFI registration (ARN: 116347). Bond investments are arranged through appropriately SEBI-registered intermediaries and are subject to credit, interest-rate and liquidity risks.',
    image:
      'https://images.pexels.com/photos/8353820/pexels-photo-8353820.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    intro: [
      'Bonds add stability and predictable coupon income to a portfolio. We help investors access government securities, PSU bonds and select corporate bonds aligned to their return, tenure and credit-risk expectations.',
      'Our guidance covers credit assessment, interest-rate risk, taxation and the role of bonds in overall diversification.',
    ],
    cards: [
      {
        icon: FileText,
        title: 'Bond Types',
        items: ['Government securities (G-Secs)', 'PSU bonds', 'AAA / AA corporate bonds', 'Tax-free bonds (secondary market)'],
      },
      {
        icon: BarChart3,
        title: 'Key Concepts',
        items: ['Coupon vs yield', 'Credit rating & risk', 'Interest-rate sensitivity', 'Maturity & duration'],
      },
      {
        icon: Target,
        title: 'Portfolio Role',
        items: ['Regular income generation', 'Equity risk offset', 'Capital preservation', 'Retirement bucket allocation'],
      },
    ],
    ctaTitle: 'Explore Bond Opportunities',
  },
  'real-estate': {
    icon: HomeIcon,
    title: 'Real Estate Guidance',
    hero: 'Selective real-estate opportunities evaluated on suitability and long-term potential.',
    note: 'Real estate is not a mutual fund product and is not covered by our AMFI registration (ARN: 116347). Any transaction is between the buyer and the respective seller or developer and is subject to their terms and applicable laws.',
    image:
      'https://images.unsplash.com/photo-1506836467174-27f1042aa48c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxmYW1pbHklMjByZXRpcmVtZW50JTIwcGxhbm5pbmd8ZW58MHx8fHwxNzg0ODkyMjc0fDA&ixlib=rb-4.1.0&q=85',
    intro: [
      'Real estate can play a role in long-term wealth building when the property is chosen with clarity on purpose, location and holding horizon. We assist clients in evaluating selected opportunities against their objectives.',
      'Our guidance focuses on requirement mapping, location due diligence, liquidity considerations and documentation coordination where applicable. We do not make guaranteed-return claims.',
    ],
    cards: [
      {
        icon: HomeIcon,
        title: 'Property Types',
        items: ['Residential (self-use)', 'Residential (investment)', 'Commercial rental', 'Plots & land'],
      },
      {
        icon: Target,
        title: 'Evaluation Framework',
        items: ['Location & connectivity', 'Builder credibility', 'Rental yield potential', 'Exit liquidity'],
      },
      {
        icon: Shield,
        title: 'Support Provided',
        items: ['Requirement assessment', 'Opportunity shortlisting', 'Documentation coordination', 'Long-term suitability review'],
      },
    ],
    ctaTitle: 'Discuss Real Estate Opportunities',
  },
  'portfolio-review': {
    icon: PieChart,
    title: 'Portfolio Review',
    hero: 'A structured second look at your existing mutual fund investments to help align them with your goals.',
    note: `This review is provided as part of our mutual fund distribution services and is not investment advice under the SEBI (Investment Advisers) Regulations, 2013. ${MF_DISCLAIMER}`,
    image:
      'https://images.pexels.com/photos/8441812/pexels-photo-8441812.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    intro: [
      "Over time, portfolios drift. New funds get added, old holdings underperform, and asset allocation moves away from what your goals actually need. Our portfolio review brings everything back into focus.",
      'We look at overlap between schemes, category concentration, cost, tax efficiency and alignment with your current life stage, and share clear observations and options.',
    ],
    cards: [
      {
        icon: PieChart,
        title: 'What We Analyse',
        items: ['Scheme-level overlap', 'Category concentration', 'Underperformance vs benchmark', 'Asset allocation drift'],
      },
      {
        icon: Target,
        title: 'Goal Alignment',
        items: ['Retirement corpus check', "Children's goals check", 'Emergency fund adequacy', 'Insurance vs investment mix'],
      },
      {
        icon: BarChart3,
        title: 'Deliverables',
        items: ['Consolidated portfolio summary', 'Rebalancing options', 'Consolidation options', 'Ongoing review cadence'],
      },
    ],
    ctaTitle: 'Get Your Portfolio Reviewed',
  },
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = SERVICES[slug];

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const HeroIcon = service.icon;

  return (
    <div className="min-h-screen" data-testid={`service-detail-${slug}`}>
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#FDF8F5]">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-3 mb-6 text-[#D97706]">
              <HeroIcon className="w-10 h-10" />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-medium text-[#A0451E] mb-6" data-testid="service-detail-title">
              {service.title}
            </h1>
            <p className="text-base font-body text-[#4A4A4A] leading-relaxed">{service.hero}</p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mb-16 items-center">
            <div>
              <img src={service.image} alt={service.title} className="rounded-2xl shadow-lg w-full h-auto" />
            </div>
            <div>
              <h2 className="text-2xl sm:text-3xl font-heading font-medium text-[#A0451E] mb-6">What We Offer</h2>
              <div className="space-y-4 text-base font-body text-[#4A4A4A] leading-relaxed">
                {service.intro.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {service.cards.map((card, index) => {
              const CardIcon = card.icon;
              return (
                <div key={index} className="bg-[#FDF8F5] rounded-xl p-8 border border-[#E5E5E5]" data-testid={`service-detail-card-${index}`}>
                  <CardIcon className="w-8 h-8 text-[#D97706] mb-4" />
                  <h3 className="text-xl font-heading font-medium text-[#A0451E] mb-4">{card.title}</h3>
                  <ul className="space-y-2 text-sm font-body text-[#4A4A4A]">
                    {card.items.map((item, i) => (
                      <li key={i} className="flex items-start">
                        <span className="w-1.5 h-1.5 bg-[#D97706] rounded-full mt-2 mr-2 flex-shrink-0"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          <p className="mt-10 text-xs font-body text-[#737373] leading-relaxed bg-[#FDF8F5] border border-[#E5E5E5] rounded-xl p-5" data-testid="service-regulatory-note">
            <strong className="text-[#A0451E]">Important:</strong> {service.note}
          </p>
        </div>
      </section>

      <section className="py-20 section-bg-secondary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-heading font-medium text-[#A0451E] mb-6">{service.ctaTitle}</h2>
          <button
            onClick={() => navigate(`/appointment?service=${encodeURIComponent(service.title.split(' ')[0] === 'Mutual' ? 'Mutual Funds' : slug === 'fixed-deposits' ? 'Fixed Deposits' : slug === 'insurance' ? 'Insurance' : slug === 'bonds' ? 'Bonds' : slug === 'real-estate' ? 'Real Estate' : slug === 'portfolio-review' ? 'Portfolio Review' : 'General Consultation')}`)}
            className="btn-primary"
            data-testid="service-detail-book-appointment-btn"
          >
            Book an Appointment
          </button>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
